/**
 * The committee's paper evaluation form, A → F, as data.
 *
 * The order here is the order on the form and the order the rail renders. The
 * panel's test asserts it, deliberately: a reviewer who knows the form should
 * not have to hunt for a section because a UI change reshuffled it.
 *
 * ⚠️ **The letters are labels; the row keys are database columns.** In 2026-09
 * the rubric moved ahead of the core evaluation areas (Louis's catch: the rubric
 * is the *evidence*, the core areas are the *judgement*) and the two sections
 * swapped letters with their positions, so the rubric is now "A" and the core
 * areas "B". Not one `key` below changed — every one of them is a column on
 * `admissions_checklist` posted through the ChartRoom contract, and renaming one
 * would write to a column that does not exist.
 */

import type { ChecklistDecision, FollowUpState, VoteValue } from "@/lib/admissions-committee/api";

/**
 * One row's comment column is NOT derivable from the row key: the core areas and
 * Section E carry one `<key>_comments` per row, the rubric carries
 * `<key>_notes` per row, and Section D carries two GROUP-level fields shared
 * across six rows (`bylaws_credentials_comments`, `bylaws_sponsorship_comments`).
 * Deriving them would write to columns that do not exist, so they are written down.
 */
export interface Row {
  key: string;
  text: string;
  kind: "tri" | "bool";
  /** Column for this row's own comment box, when it has one. */
  comment?: string;
  /** What the comment box asks for, when "Comments" would leave the reviewer guessing. */
  placeholder?: string;
  /**
   * Section E: a Yes here is a request to staff, and only reaches them once a
   * member has written the ask in `comment` and pressed Confirm. See
   * `isFollowUpConfirmed`.
   */
  followUp?: boolean;
  /**
   * This row is the conclusion of the rubric, not an independent judgement.
   *
   * Declared here rather than hard-coded in the rail so there is one place the
   * pairing lives. A committee that said "the work has no formal context" and
   * "the work experience is approved" was making two contradictory claims about
   * the same fact, and it was the second one that travelled onward.
   *
   * `from` lists EVERY row that answers this one, and they answer it as an AND:
   * all of them Yes makes it Yes, ANY of them No makes it No (even while the
   * rest are still blank — a No does not become less true by being joined
   * later), and otherwise it stays undefined and the row is locked. Only the
   * first core area is derived; the others are the committee's own
   * judgements and carry no `derivedFrom` at all.
   */
  derivedFrom?: {
    /** Every rubric row that decides this one, together. */
    from: string[];
    /** What this row becomes when the rubric passes / fails. */
    yes: unknown;
    no: unknown;
    /** Column holding the written reason for departing from the derivation. */
    reason: string;
  };
}

export interface GroupComment {
  after: string;
  field: string;
  placeholder: string;
  /** The rows this one box covers — used to decide whether to reveal it. */
  rows: string[];
}

export interface Section {
  key: string;
  title: string;
  rows: Row[];
  /**
   * Which answer makes a row's comment box worth showing.
   *
   * Comment boxes used to be permanently open on all seventeen rows, which is
   * most of the rail's height spent on empty textareas. They now appear on the
   * answer that has to be justified — "No" or "Needs More Info" — because that
   * is the answer the committee will be asked to explain later, to the board or
   * to the applicant. A "Yes" needs no defence; every other answer does. Use
   * "+ Add note" to comment on one anyway.
   *
   * That answer is "Yes" for Section E, whose questions run the other way:
   * "Additional documentation required? No" is the clean answer and needs no
   * comment, while a "Yes" has to say which documents.
   */
  revealComment: (answer: unknown) => boolean;
  /** Column for a comment box covering the whole group, when the schema shares one. */
  groupComments?: GroupComment[];
  /**
   * This section reads the one named here: it cannot be answered until that
   * section is, because that section is its evidence.
   */
  dependsOn?: string;
  /**
   * A failed row in this section forces Section F to Defer.
   *
   * Set on the sections that judge the *application*: the rubric, the core areas
   * and the bylaws. Deliberately NOT set on Section E, where the questions run
   * the other way — "Additional documentation required? No" is a clean
   * application, and treating it as a failure would defer every one of them.
   */
  defersOnFail?: boolean;
  /**
   * Groups of rows where answering ONE of them Yes satisfies the whole group.
   *
   * Only the bylaws credentials rows need this: §2.01(C) is satisfied by five
   * years of experience **or** three years with all three CMT levels, so a
   * Path A applicant answers the second row No as a matter of course. Without
   * this, "any No defers" would defer every applicant alive.
   */
  eitherOr?: string[][];
}

const failedTri = (a: unknown) => a === "no" || a === "needs_info";
const failedBool = (a: unknown) => a === false;

/** The rubric's four tests, in form order — they answer B's first row together. */
export const RUBRIC_ROWS = [
  "test_context",
  "test_outputs",
  "test_accountability",
  "test_cadence",
];

/** The rubric tests that together answer one core evaluation area. */
const derives = (from: string[], reason: string) => ({ from, yes: "yes", no: "no", reason });

export const SECTIONS: Section[] = [
  {
    // FIRST on the form, and therefore lettered A, since 2026-09. The rubric is
    // the evidence; the core areas below are the judgement it supports. Only the
    // letter and the title moved — every row key here is a DB column.
    key: "A",
    title: "A · Professional Experience Rubric",
    revealComment: failedBool,
    defersOnFail: true,
    rows: [
      { key: "test_context", text: "Context — a formal arrangement", kind: "bool", comment: "test_context_notes" },
      { key: "test_outputs", text: "Outputs — decision-useful deliverables", kind: "bool", comment: "test_outputs_notes" },
      { key: "test_accountability", text: "Accountability — supervised, reviewed or mandated", kind: "bool", comment: "test_accountability_notes" },
      { key: "test_cadence", text: "Cadence — regular and sustained", kind: "bool", comment: "test_cadence_notes" },
    ],
  },
  {
    // The rubric's four tests answer the FIRST row here and only that row:
    // between them they establish one thing — whether the claimed experience is
    // approved professional work — so all four pass or the row is No.
    //
    // The other two are the committee's own judgements from the packet.
    // Competence and conduct are not things the rubric measures, and deriving
    // them from it (as this section did until 2026-09) put conclusions on the
    // record that nobody had actually reached.
    //
    // "Commitment to the TA Profession/Community" was dropped from the form in
    // 2026-09 at the committee's request: it is not a membership requirement in
    // §2.01(C) and was being answered Yes as a formality. The
    // `area_commitment` COLUMN still exists on `admissions_checklist` and still
    // travels through the ChartRoom contract — it is simply never answered, so
    // don't drop it from the schema or the payload.
    key: "B",
    title: "B · Core Evaluation Areas",
    revealComment: failedTri,
    dependsOn: "A",
    defersOnFail: true,
    rows: [
      {
        key: "area_work_experience",
        text: "Approved Professional Work Experience",
        kind: "tri",
        comment: "area_work_experience_comments",
        derivedFrom: derives(RUBRIC_ROWS, "area_work_experience_comments"),
      },
      // Independent judgements — deliberately NOT derived. See the note above.
      {
        key: "area_competence",
        text: "Competence in Technical Analysis",
        kind: "tri",
        comment: "area_competence_comments",
      },
      {
        key: "area_ethics",
        text: "Ethical & Professional Conduct",
        kind: "tri",
        comment: "area_ethics_comments",
      },
    ],
  },
  {
    key: "D",
    title: "D · Bylaws Compliance §2.01(C)",
    revealComment: failedBool,
    defersOnFail: true,
    eitherOr: [["bylaws_credentials_5yr", "bylaws_credentials_3yr_cmt3"]],
    rows: [
      { key: "bylaws_credentials_5yr", text: "5 years' approved experience", kind: "bool" },
      { key: "bylaws_credentials_3yr_cmt3", text: "or 3 years with all three CMT levels", kind: "bool" },
      { key: "bylaws_credentials_within_3yr", text: "Fulfilled within 3 years of Level III", kind: "bool" },
      { key: "bylaws_sponsorship_min3", text: "At least three references named", kind: "bool" },
      { key: "bylaws_sponsorship_2senior", text: "At least two senior members in good standing", kind: "bool" },
      { key: "bylaws_sponsorship_employer_cap", text: "No more than one sponsor from the applicant's employer", kind: "bool" },
    ],
    groupComments: [
      {
        after: "bylaws_credentials_within_3yr",
        field: "bylaws_credentials_comments",
        placeholder: "Comments on credentials (i)",
        rows: ["bylaws_credentials_5yr", "bylaws_credentials_3yr_cmt3", "bylaws_credentials_within_3yr"],
      },
      {
        after: "bylaws_sponsorship_employer_cap",
        field: "bylaws_sponsorship_comments",
        placeholder: "Comments on sponsorship (ii)",
        rows: ["bylaws_sponsorship_min3", "bylaws_sponsorship_2senior", "bylaws_sponsorship_employer_cap"],
      },
    ],
  },
  {
    key: "E",
    title: "E · Committee Considerations",
    // Inverted questions: the box opens on Yes. See `revealComment`.
    revealComment: (a) => a === true,
    rows: [
      {
        key: "consider_additional_docs",
        text: "Additional documentation required?",
        kind: "bool",
        comment: "consider_additional_docs_comments",
        placeholder: "Exactly which documents the applicant must send, e.g. “Signed employer attestation from Acme Capital covering 2019–2023”.",
        followUp: true,
      },
      {
        key: "consider_clarification",
        text: "Clarification needed?",
        kind: "bool",
        comment: "consider_clarification_comments",
        placeholder: "The question to put to the applicant, as you want it asked.",
        followUp: true,
      },
      {
        key: "consider_exceptional",
        text: "Exceptional contribution case?",
        kind: "bool",
        comment: "consider_exceptional_comments",
        placeholder: "Why the contribution warrants an exception",
        followUp: true,
      },
    ],
  },
];

export const sectionByKey = (key: string) => SECTIONS.find((s) => s.key === key)!;

/** Every row across A/B/D/E, for the dock's "answered n of m" progress. */
export const ALL_ROWS = SECTIONS.flatMap((s) => s.rows);

/** A row's own wording, by column name — for naming one row on another. */
export const rowText = (key: string) => ALL_ROWS.find((r) => r.key === key)?.text ?? key;

export const DOC_TYPES: Array<[string, string]> = [
  ["employer_attestation", "Employer/client attestations"],
  ["hr_contract", "HR contracts, offer letters, pay records"],
  ["professional_resume", "Professional resume / CV"],
  ["no_comp_statement", "Signed no-compensation statement"],
  ["work_outputs", "Work outputs (reports, models, syllabi, broadcasts)"],
  ["oversight_records", "Oversight/cadence records"],
  ["other", "Other"],
];

export const DECISIONS: Array<[ChecklistDecision, string]> = [
  ["approve", "Approve"],
  ["defer", "Defer"],
  ["flag_board", "Flag for Board Review"],
];

export const VOTES: Array<[VoteValue, string]> = [
  ["approve", "Approve"],
  ["defer", "Defer"],
  ["deny", "Deny"],
];

/** The options a row offers, in form order. */
export const optionsFor = (kind: Row["kind"]): Array<[string | boolean, string]> =>
  kind === "tri"
    ? [
        ["yes", "Yes"],
        ["no", "No"],
        ["needs_info", "Needs More Info"],
      ]
    : [
        [true, "Yes"],
        [false, "No"],
      ];

/** A row counts as answered once it holds one of its own options. */
export const isAnswered = (row: Row, fields: Record<string, unknown>) =>
  optionsFor(row.kind).some(([value]) => fields[row.key] === value);

// ── The rubric (A) drives ONE core area (B's first row) ─────────────────────
//
// The four rubric tests answer "Approved Professional Work Experience" together
// and answer nothing else. Three states, and the rail renders a message for each:
//   incomplete → not every test is in and none failed, so the row is locked.
//   answered   → all four passed (Yes), or one failed (No).
//   departed   → the member chose something else, which needs a written reason.

const hasText = (v: unknown) => typeof v === "string" && v.trim() !== "";

/** Every core-area row that is answered by the rubric. */
export const DERIVED_ROWS = ALL_ROWS.filter((r) => r.derivedFrom);

/** The rubric rows that answer this one, with their current answers. */
export const derivationSources = (row: Row, fields: Record<string, unknown>) =>
  (row.derivedFrom?.from ?? []).map((key) => ({ key, value: fields[key] }));

/** The first rubric test this row failed on, when it failed one. */
export const failedSource = (row: Row, fields: Record<string, unknown>) =>
  derivationSources(row, fields).find((s) => s.value === false)?.key;

/**
 * What this row should hold, or `undefined` while the rubric is incomplete.
 *
 * An AND across the tests, and both directions derive: every test Yes makes the
 * area Yes, ANY test No makes it No — the rest of the rubric cannot rescue a
 * failed test, so the answer lands as soon as one comes back No. The committee
 * can depart from either, which is what the reason is for.
 */
export const derivedAnswer = (row: Row, fields: Record<string, unknown>) => {
  if (!row.derivedFrom) return undefined;
  const sources = derivationSources(row, fields);
  if (sources.some((s) => s.value === false)) return row.derivedFrom.no;
  if (sources.length > 0 && sources.every((s) => s.value === true)) return row.derivedFrom.yes;
  return undefined;
};

/** A derived row nobody may answer by hand yet, because its evidence is blank. */
export const isLockedRow = (row: Row, fields: Record<string, unknown>) =>
  !!row.derivedFrom && derivedAnswer(row, fields) === undefined;

/**
 * The member answered a derived row differently from what the rubric says.
 *
 * Permitted — the rubric informs the judgement, it does not replace the
 * committee — but it has to be explained, in the row's own comment column so
 * the reason travels with the checklist and the backend can enforce the same
 * rule at finalise.
 */
export const isDerivationOverride = (row: Row, fields: Record<string, unknown>) => {
  const derived = derivedAnswer(row, fields);
  return derived !== undefined && isAnswered(row, fields) && fields[row.key] !== derived;
};

export const overrideNeedsReason = (row: Row, fields: Record<string, unknown>) =>
  isDerivationOverride(row, fields) && !hasText(fields[row.derivedFrom!.reason]);

/** Every derived row across the form whose departure is still unexplained. */
export const unexplainedOverrides = (fields: Record<string, unknown>) =>
  DERIVED_ROWS.filter((row) => overrideNeedsReason(row, fields));

// ── Any failed row defers the application (Section F) ────────────────────────

/** Did this row come back as a failure, allowing for either/or groups? */
const rowFailed = (section: Section, row: Row, fields: Record<string, unknown>) => {
  if (!section.revealComment(fields[row.key])) return false;
  // A row inside an either/or group only fails when the whole group does.
  const group = section.eitherOr?.find((g) => g.includes(row.key));
  if (group) return group.every((key) => fields[key] === false);
  return true;
};

/**
 * Every answered-No row that forces Section F to Defer, with its section letter.
 *
 * The committee may still record Approve over the top of these — this is a
 * default, not a verdict — but doing so needs a written rationale, because
 * approving an application that failed something is exactly the decision the
 * board or the applicant will later ask about.
 */
export const deferTriggers = (fields: Record<string, unknown>) =>
  SECTIONS.filter((s) => s.defersOnFail).flatMap((section) =>
    section.rows
      .filter((row) => rowFailed(section, row, fields))
      .map((row) => ({ section: section.key, key: row.key, text: row.text })),
  );

// ── Section E: a Yes is a request to staff, sent only once confirmed ────────
//
// The note autosaves as it is typed, so it cannot be what sends the request —
// a half-written ask would go out. Confirming is a separate, explicit act, and
// any later edit withdraws it. Mirrors `lifecycle.FOLLOWUP_ROWS` /
// `FOLLOWUP_NOTE_MIN_CHARS` in the backend; the two must agree.

/** The shortest ask staff could act on. Same number as the backend. */
export const FOLLOWUP_NOTE_MIN_CHARS = 10;

export const FOLLOWUP_ROWS = SECTIONS.flatMap((s) => s.rows).filter((r) => r.followUp);

export const followUpNoteReady = (row: Row, fields: Record<string, unknown>) =>
  String(fields[row.comment!] ?? "").trim().length >= FOLLOWUP_NOTE_MIN_CHARS;

/**
 * This row's ask has been confirmed, and nothing on screen has changed since.
 * Compared against the confirmed text rather than trusted from the server, so
 * an edit reads as unconfirmed the moment it is typed, not after the autosave.
 */
export const isFollowUpConfirmed = (
  row: Row,
  fields: Record<string, unknown>,
  followups: Record<string, FollowUpState> | undefined,
) => {
  const state = followups?.[row.key];
  return (
    fields[row.key] === true &&
    !!state?.confirmed_at &&
    String(fields[row.comment!] ?? "").trim() === String(state.note ?? "").trim()
  );
};

/** Section E rows answered Yes whose ask has not been confirmed. Blocks Finalise. */
export const unconfirmedFollowups = (
  fields: Record<string, unknown>,
  followups: Record<string, FollowUpState> | undefined,
) => FOLLOWUP_ROWS.filter((row) => fields[row.key] === true && !isFollowUpConfirmed(row, fields, followups));
